import { useState } from "react";
import { Lock, Loader2, AtSign } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function LoginScreen() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Login failed";
      setError(typeof msg === "string" ? msg : "Login failed");
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full h-screen bg-black flex items-center justify-center p-4" data-testid="login-screen">
      <form
        onSubmit={submit}
        className="border border-[#2D7544] bg-[#050805] w-full max-w-md shadow-[6px_6px_0px_#00FF41]"
        data-testid="login-form"
      >
        <div className="border-b border-[#2D7544] px-5 py-4 flex items-center gap-2">
          <Lock size={14} className="text-[#00FF41]" />
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#66B07A]">authentication required</div>
        </div>

        <div className="p-6 flex flex-col gap-5">
          <div>
            <div className="text-2xl font-extrabold text-[#00FF41] dv-glow leading-none" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              CxAssistant<span className="dv-glow">_</span>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#3D8657] mt-2">
              &gt; sign in with your account
            </div>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="dv-overline">Email</span>
            <input
              data-testid="login-email-input"
              type="email"
              autoFocus
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="dv-input"
              placeholder="you@example.com"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="dv-overline">Password</span>
            <input
              data-testid="login-password-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="dv-input"
              placeholder="••••••••"
            />
          </label>

          {error && (
            <div className="text-[11px] text-[#FF3344] font-mono" data-testid="login-error">
              &gt; {error}
            </div>
          )}
        </div>

        <div className="border-t border-[#2D7544] px-5 py-3 flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#3D8657]">
            session ttl :: 30d
          </div>
          <button
            type="submit"
            disabled={busy || !email || !password}
            className="dv-btn-primary flex items-center gap-2"
            data-testid="login-submit-btn"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <AtSign size={13} />}
            Sign in
          </button>
        </div>
      </form>
    </div>
  );
}
